// Connection status banner.
//
// The app should say when network or account reachability drops. When `auth.isOffline()` is true
// the stored session is intact and the app keeps retrying in the background; this banner tells the
// user what failed.
//
// Fixed + centered so it overlays whatever screen is up (chat, an app panel, settings) without
// shifting layout. Deliberately reassuring: "still signed in" is the whole point of the fix.

import { createEffect, createSignal, onCleanup, Show, type Component } from "solid-js"
import { useAuth } from "@/context/auth"
import {
  AUTH_CONNECTION_BANNER_DEBOUNCE_MS,
  authConnectionBannerText,
  authConnectionDebugLabel,
  shouldDebounceAuthConnectionIssue,
} from "@/context/auth/connection-issue"
import type { AuthConnectionIssue } from "@/context/auth/types"

const legacyConnectionIssue = (): AuthConnectionIssue => ({
  reason: "openanalyst_account_service_unreachable",
  operation: "openanalyst_account",
  retryDelayMs: null,
  firstDetectedAt: 0,
  lastDetectedAt: 0,
})

export const NetworkStatusBanner: Component = () => {
  const auth = useAuth()
  const [visibleIssue, setVisibleIssue] = createSignal<AuthConnectionIssue | null>(null)
  let timer: ReturnType<typeof setTimeout> | undefined

  const clearTimer = () => {
    if (!timer) return
    clearTimeout(timer)
    timer = undefined
  }

  const currentIssue = () =>
    auth.isOffline() ? auth.connectionIssue?.() ?? auth.state().connectionIssue ?? legacyConnectionIssue() : null

  createEffect(() => {
    clearTimer()
    const issue = currentIssue()
    if (!issue) {
      setVisibleIssue(null)
      return
    }
    if (!shouldDebounceAuthConnectionIssue(issue)) {
      setVisibleIssue(issue)
      return
    }
    setVisibleIssue(null)
    timer = setTimeout(() => setVisibleIssue(issue), AUTH_CONNECTION_BANNER_DEBOUNCE_MS)
  })

  onCleanup(clearTimer)

  return (
    <Show when={visibleIssue()} keyed>
      {(issue) => {
        const text = authConnectionBannerText(issue)
        return (
          <div
            data-component="network-status-banner"
            data-auth-connection-reason={issue.reason}
            data-auth-connection-operation={issue.operation}
            role="status"
            aria-live="polite"
            title={authConnectionDebugLabel(issue)}
            class="pointer-events-none fixed inset-x-0 top-3 z-[9999] flex justify-center px-4"
          >
            <div class="pointer-events-auto flex items-center gap-2.5 rounded-full border border-border-weaker-base bg-surface-raised-base px-4 py-2 shadow-lg-border-base">
              <span class="relative flex size-2.5 shrink-0">
                <span class="absolute inline-flex size-full animate-ping rounded-full bg-icon-warning-base opacity-70" />
                <span class="relative inline-flex size-2.5 rounded-full bg-icon-warning-base" />
              </span>
              <span class="text-13-medium text-text-strong">{text.title}</span>
              <span class="text-13-regular text-text-base">- {text.message}</span>
            </div>
          </div>
        )
      }}
    </Show>
  )
}
